var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import '../button/button.js';
/**
 * A full-screen file preview overlay.
 *
 * Supports image files with zoom/pan, video with native playback controls,
 * PDFs via iframe, text files inline, and a download fallback for other file
 * types. Use the static `open()` method to show the preview imperatively.
 *
 * @fires close - Fired when the preview is closed
 * @fires download - Fired when the download button is clicked. Detail: `{ src, name }`
 *
 * @example
 * ```ts
 * import { KRFilePreview } from '@kodaris/krubble-components/file-preview';
 *
 * KRFilePreview.open({ src: '/files/photo.png', name: 'photo.png' });
 * ```
 */
export class KRFilePreview extends LitElement {
    constructor() {
        super(...arguments);
        this._zoom = 100;
        this._panX = 0;
        this._panY = 0;
        this._dragging = false;
        this._dragStartX = 0;
        this._dragStartY = 0;
        this._panStartX = 0;
        this._panStartY = 0;
        /** URL of the file to preview */
        this.src = '';
        /** Display name shown in the toolbar */
        this.name = '';
        this._zoomLevel = 100;
        this._textContent = null;
        this._handleKeydown = (e) => {
            if (e.key === 'Escape') {
                this._close();
            }
        };
        this._close = () => {
            this.dispatchEvent(new CustomEvent('close', {
                bubbles: true,
                composed: true
            }));
            if (KRFilePreview._activePreview === this) {
                KRFilePreview._activePreview = null;
            }
            this.remove();
        };
        this._handleDragMove = (e) => {
            if (!this._dragging) {
                return;
            }
            this._panX = this._panStartX + (e.clientX - this._dragStartX);
            this._panY = this._panStartY + (e.clientY - this._dragStartY);
            this.requestUpdate();
        };
        this._handleDragEnd = () => {
            this._dragging = false;
            document.removeEventListener('mousemove', this._handleDragMove);
            document.removeEventListener('mouseup', this._handleDragEnd);
        };
    }
    /**
     * Open a file preview overlay.
     * @param options - The file source URL and display name
     * @returns The created file-preview element
     */
    static open(options) {
        if (this._activePreview) {
            this._activePreview._close();
        }
        const preview = document.createElement('kr-file-preview');
        preview.src = options.src;
        preview.name = options.name;
        document.body.appendChild(preview);
        this._activePreview = preview;
        return preview;
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('keydown', this._handleKeydown);
        if (this._isText()) {
            this._loadTextContent();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this._handleKeydown);
    }
    _handleDownload() {
        this.dispatchEvent(new CustomEvent('download', {
            bubbles: true,
            composed: true,
            detail: {
                src: this.src,
                name: this.name
            },
        }));
    }
    _handleZoomIn() {
        this._zoomLevel = Math.min(this._zoomLevel + 25, 300);
        if (this._zoomLevel <= 100) {
            this._panX = 0;
            this._panY = 0;
        }
    }
    _handleZoomOut() {
        this._zoomLevel = Math.max(this._zoomLevel - 25, 25);
        if (this._zoomLevel <= 100) {
            this._panX = 0;
            this._panY = 0;
        }
    }
    _handleZoomReset() {
        this._zoomLevel = 100;
        this._panX = 0;
        this._panY = 0;
    }
    _handleDragStart(e) {
        if (this._zoomLevel <= 100) {
            return;
        }
        e.preventDefault();
        this._dragging = true;
        this._dragStartX = e.clientX;
        this._dragStartY = e.clientY;
        this._panStartX = this._panX;
        this._panStartY = this._panY;
        document.addEventListener('mousemove', this._handleDragMove);
        document.addEventListener('mouseup', this._handleDragEnd);
    }
    _handleBackdropClick(e) {
        if (e.target === e.currentTarget) {
            this._close();
        }
    }
    _getExtension() {
        return (this.name || '').split('.').pop()?.toLowerCase() || '';
    }
    _isImage() {
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(this._getExtension());
    }
    _isText() {
        return ['csv', 'txt', 'json', 'xml', 'log', 'md', 'yml', 'yaml', 'ini', 'cfg', 'conf', 'env', 'sh', 'bat'].includes(this._getExtension());
    }
    _loadTextContent() {
        fetch(this.src)
            .then(response => response.text())
            .then(text => {
            this._textContent = text;
            if (this._getExtension() !== 'csv') {
                return;
            }
            // CSV renders in a <pre>, so pad every cell out to its column's widest
            // value — that's what makes the columns line up as plain text.
            const lines = text.trim().split('\n');
            if (!lines.length) {
                return;
            }
            const rows = lines.map(line => line.split(',').map(cell => cell.trim()));
            const colCount = Math.max(...rows.map(row => row.length));
            const colWidths = [];
            for (let c = 0; c < colCount; c++) {
                let max = 0;
                for (const row of rows) {
                    const len = (row[c] || '').length;
                    if (len > max) {
                        max = len;
                    }
                }
                colWidths.push(max);
            }
            this._textContent = rows.map(row => {
                return row.map((cell, i) => cell.padEnd(colWidths[i] || 0)).join('   ');
            }).join('\n');
        })
            .catch(() => {
            this._textContent = 'Failed to load file content.';
        });
    }
    _renderFallbackIcon(ext) {
        if (['doc', 'docx', 'rtf', 'txt'].includes(ext)) {
            return html `<svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm2-6h8v2H8v-2zm0-4h8v2H8v-2zm0 8h5v2H8v-2z"/>
        </svg>`;
        }
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
            return html `<svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 2v3H5V5h14zm0 5v4H5v-4h14zM5 19v-3h14v3H5zm2-8h4v2H7v-2zm0 5h4v2H7v-2z"/>
        </svg>`;
        }
        if (['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) {
            return html `<svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10zm-8-4h2v2h-2v-2zm0-4h2v2h-2v-2zm-2 2h2v2h-2v-2z"/>
        </svg>`;
        }
        return html `<svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
      </svg>`;
    }
    render() {
        let content;
        if (this._isImage()) {
            const scale = this._zoomLevel / 100;
            const translateX = this._panX / scale;
            const translateY = this._panY / scale;
            let cursor = 'default';
            if (this._zoomLevel > 100) {
                cursor = 'grab';
            }
            content = html `<img
          class="body__image"
          src="${this.src}"
          alt="${this.name}"
          style=${styleMap({
                transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
                cursor: cursor
            })}
          @mousedown=${this._handleDragStart}
        />`;
        }
        else if (['mp4', 'webm', 'ogg', 'mov'].includes(this._getExtension())) {
            // Native controls only — no zoom/pan toolbar, since scrubbing and volume are
            // what a video needs and the browser already provides them. `.mov` files fall
            // back to the download tile below if the browser can't decode the codec.
            content = html `<video
          class="body__video"
          src="${this.src}"
          controls
          autoplay
          playsinline
        ></video>`;
        }
        else if (this._getExtension() === 'pdf') {
            content = html `<iframe class="body__iframe" src="${this.src}"></iframe>`;
        }
        else if (this._isText()) {
            if (this._textContent === null) {
                content = html `<div class="body__text-loading">Loading...</div>`;
            }
            else {
                content = html `<pre class="body__text">${this._textContent}</pre>`;
            }
        }
        else {
            const ext = this._getExtension();
            // The fallback tile is colored by file family; anything unrecognized is default.
            let iconClass = 'fallback__icon--default';
            if (['doc', 'docx', 'rtf', 'txt'].includes(ext)) {
                iconClass = 'fallback__icon--doc';
            }
            if (['xls', 'xlsx', 'csv'].includes(ext)) {
                iconClass = 'fallback__icon--xls';
            }
            if (['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) {
                iconClass = 'fallback__icon--zip';
            }
            content = html `
        <div class="fallback">
          <div class=${classMap({
                'fallback__icon': true,
                [iconClass]: true
            })}>
            ${this._renderFallbackIcon(ext)}
          </div>
          <div class="fallback__name">${this.name}</div>
          <div class="fallback__hint">No preview available for this file type</div>
          <kr-button color="primary" @click=${this._handleDownload}>
            <svg
              class="fallback__btn"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 7h14v2H5v-2z"/>
            </svg>
            Download
          </kr-button>
        </div>
      `;
        }
        return html `
      <div class="toolbar">
        <div class="toolbar__name">${this.name}</div>
        <div class="toolbar__controls">
          ${this._isImage() ? html `
            <button
              class="toolbar__btn"
              @click=${this._handleZoomOut}
              title="Zoom out"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM7 9h5v1H7V9z"/>
              </svg>
            </button>
            <div class="toolbar__zoom-label" @click=${this._handleZoomReset}>${this._zoomLevel}%</div>
            <button
              class="toolbar__btn"
              @click=${this._handleZoomIn}
              title="Zoom in"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zm-2-5h2V7h1v2h2v1h-2v2H9v-2H7V9z"/>
              </svg>
            </button>
            <div class="toolbar__separator"></div>
          ` : nothing}
          <button
            class="toolbar__btn"
            @click=${this._handleDownload}
            title="Download"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 7h14v2H5v-2z"/>
            </svg>
          </button>
          <button
            class="toolbar__btn"
            @click=${this._close}
            title="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </div>
      <div class="body" @click=${this._handleBackdropClick}>
        ${content}
      </div>
    `;
    }
}
KRFilePreview.styles = css `
    *,
    *::after,
    *::before,
    :host {
      box-sizing: border-box;
    }

    :host {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      flex-direction: column;
      z-index: 10000;
    }

    .toolbar {
      display: flex;
      align-items: center;
      height: 56px;
      padding: 0 24px;
      flex-shrink: 0;
    }

    .toolbar__name {
      color: white;
      font-size: 16px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-right: auto;
    }

    .toolbar__controls {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .toolbar__btn {
      width: 40px;
      height: 40px;
      border: none;
      border-radius: 50%;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }

    .toolbar__btn:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    .toolbar__btn svg {
      width: 24px;
      height: 24px;
      fill: white;
    }

    .toolbar__zoom-label {
      color: white;
      font-size: 13px;
      min-width: 40px;
      text-align: center;
      user-select: none;
      cursor: pointer;
    }

    .toolbar__separator {
      width: 1px;
      height: 24px;
      background: rgba(255, 255, 255, 0.25);
      margin: 0 8px;
    }

    .body {
      flex: 1;
      overflow: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 36px 36px 36px;
    }

    .body__image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      transition: transform 0.15s ease;
      transform-origin: center center;
    }

    /* A definite box, NOT max-width/max-height: a <video> has no intrinsic size
       until its metadata loads, so sizing to content means the browser lays it out
       at the default 300x150, shows the spinner in that tiny box, then reflows once
       the real dimensions arrive. Fixing the box up front (same as .body__iframe)
       keeps the player one size from open to playback; object-fit letterboxes the
       frame inside it against the black background. */
    .body__video {
      width: 80vw;
      height: 100%;
      object-fit: contain;
      background: black;
      border-radius: 4px;
    }

    .body__iframe {
      width: 80vw;
      height: 100%;
      border: 1px solid #5e5e5e;
      border-radius: 4px;
      background: white;
    }

    .body__text {
      width: 80vw;
      max-height: 100%;
      overflow: auto;
      background: white;
      color: #000;
      border-radius: 4px;
      padding: 24px;
      font-family: monospace;
      font-size: 14px;
      line-height: 1.6;
      white-space: pre;
      margin: 0;
      tab-size: 4;
    }

    .body__text-loading {
      color: #888;
      font-style: italic;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
    }

    .fallback {
      background: white;
      border-radius: 12px;
      padding: 48px 64px;
      text-align: center;
      color: #000;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }

    .fallback__icon {
      width: 72px;
      height: 72px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .fallback__icon svg {
      width: 36px;
      height: 36px;
    }

    .fallback__icon--doc { background: #dbeafe; color: #2563eb; }
    .fallback__icon--xls { background: #d1fae5; color: #16a34a; }
    .fallback__icon--zip { background: #fef3c7; color: #b45309; }
    .fallback__icon--default { background: #f4f5f7; color: #555; }

    .fallback__name {
      font-size: 18px;
      font-weight: 600;
      max-width: 320px;
      word-break: break-all;
    }

    .fallback__hint {
      font-size: 14px;
      color: black;
      margin-bottom: 6px;
    }

    .fallback__btn svg {
      width: 18px;
      height: 18px;
      fill: currentColor;
    }
  `;
// eslint-disable-next-line kodaris/component-name-context
KRFilePreview._activePreview = null;
__decorate([
    property({ type: String })
], KRFilePreview.prototype, "src", void 0);
__decorate([
    property({ type: String })
], KRFilePreview.prototype, "name", void 0);
__decorate([
    state()
], KRFilePreview.prototype, "_zoomLevel", void 0);
__decorate([
    state()
], KRFilePreview.prototype, "_textContent", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-file-preview')) {
    customElements.define('kr-file-preview', KRFilePreview);
}
//# sourceMappingURL=file-preview.js.map